import React, { createContext, useContext, useState, useCallback } from 'react';
import { captureError } from '@/utils/errorHandler';

const ErrorContext = createContext(null);

export function ErrorProvider({ children, authFetch }) {
  const [errors, setErrors] = useState([]);
  const [summary, setSummary] = useState({ total: 0, new: 0, acknowledged: 0, resolved: 0 });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false,
  });
  const [filters, setFilters] = useState({
    status: '',
    source: '',
    search: '',
    appId: '',
  });
  const [activeErrorId, setActiveErrorId] = useState(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [loading, setLoading] = useState(false);

  // Save scroll position to context and sessionStorage
  const saveScrollPosition = useCallback(() => {
    const position = window.scrollY || document.documentElement.scrollTop;
    setScrollPosition(position);
    try {
      sessionStorage.setItem('errorListScrollPosition', position.toString());
    } catch (e) {
      console.warn('Failed to save scroll position:', e);
    }
  }, []);

  // Restore scroll position from context or sessionStorage
  const restoreScrollPosition = useCallback((targetPosition = null) => {
    const position = targetPosition !== null 
      ? targetPosition 
      : scrollPosition || parseInt(sessionStorage.getItem('errorListScrollPosition') || '0', 10);
    
    if (position > 0) {
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        window.scrollTo({ top: position, behavior: 'smooth' });
      });
    }
  }, [scrollPosition]);

  // Load errors with filters
  const loadErrors = useCallback(async (filterParams = {}) => {
    try {
      setLoading(true);
      
      // Use provided filters or fall back to current filters
      const mergedFilters = {
        status: filterParams.status !== undefined ? filterParams.status : filters.status,
        source: filterParams.source !== undefined ? filterParams.source : filters.source,
        search: filterParams.search !== undefined ? filterParams.search : filters.search,
        appId: filterParams.appId !== undefined ? filterParams.appId : filters.appId,
      };
      
      // Update filters if they changed
      if (JSON.stringify(mergedFilters) !== JSON.stringify(filters)) {
        setFilters(mergedFilters);
      }
      
      const page = filterParams.page !== undefined ? filterParams.page : (pagination.page || 1);
      let url = `/api/admin/errors?page=${page}&limit=50`;
      
      if (mergedFilters.status) url += `&status=${mergedFilters.status}`;
      if (mergedFilters.source) url += `&source=${mergedFilters.source}`;
      if (mergedFilters.search) url += `&search=${encodeURIComponent(mergedFilters.search)}`;
      if (mergedFilters.appId) url += `&appId=${mergedFilters.appId}`;
      
      const response = await authFetch(url);
      
      if (!response.ok) {
        throw new Error('Failed to load errors');
      }

      const data = await response.json();
      setErrors(data.errors || []);
      setSummary(data.summary || { total: 0, new: 0, acknowledged: 0, resolved: 0 });
      setPagination(data.pagination || {
        page: 1,
        limit: 50,
        total: 0,
        totalPages: 0,
        hasNextPage: false,
        hasPrevPage: false,
      });
      
      return data;
    } catch (error) {
      captureError(error, {
        action: 'loadErrors',
        component: 'ErrorContext',
        extra: { filters, page: pagination.page },
      });
      console.error('Error loading errors:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [authFetch, filters, pagination.page]);

  // Clear active error after highlight timeout
  const clearActiveError = useCallback(() => {
    const timeoutId = setTimeout(() => {
      setActiveErrorId(null);
    }, 3000);
    return () => clearTimeout(timeoutId);
  }, [setActiveErrorId]);

  const value = {
    errors,
    summary,
    pagination,
    filters,
    activeErrorId,
    scrollPosition,
    loading,
    setErrors,
    setSummary,
    setPagination,
    setFilters,
    setActiveErrorId,
    setScrollPosition,
    loadErrors,
    saveScrollPosition,
    restoreScrollPosition,
    clearActiveError,
  };

  return (
    <ErrorContext.Provider value={value}>
      {children}
    </ErrorContext.Provider>
  );
}

export function useErrorContext() {
  const context = useContext(ErrorContext);
  if (!context) {
    throw new Error('useErrorContext must be used within ErrorProvider');
  }
  return context;
}
